import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import kkpService from '../../services/kkpService';
import { formatBriefFileSize } from '../../utils/kkpBriefFichierUtils';
import { AppButton, AppSpinner } from '../ui';
import '../../styles/kkp-brief-fichiers.scss';

function fileIcon(mime = '') {
  if (mime.startsWith('image/')) return 'mdi:file-image-outline';
  if (mime.startsWith('video/')) return 'mdi:file-video-outline';
  if (mime === 'application/pdf') return 'mdi:file-pdf-box';
  if (mime.startsWith('text/')) return 'mdi:file-document-outline';
  return 'mdi:paperclip';
}

const KkpBriefAttachmentsViewer = ({
  venteId,
  piecesJointes = [],
  onError,
  fetchBlob,
  downloadFile,
  title = 'Pièces jointes',
}) => {
  const items = useMemo(() => piecesJointes ?? [], [piecesJointes]);
  const [previewUrls, setPreviewUrls] = useState({});
  const [loadingPreviewId, setLoadingPreviewId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    setPreviewUrls({});
  }, [venteId, items]);

  useEffect(() => () => {
    Object.values(previewUrls).forEach((url) => URL.revokeObjectURL(url));
  }, [previewUrls]);

  const loadPreview = async (item) => {
    if (!venteId || previewUrls[item.id] || (!item.is_image && !item.is_video)) return;

    setLoadingPreviewId(item.id);
    try {
      const blobFn = fetchBlob ?? ((fileId, opts) => kkpService.fetchBriefFichierBlob(venteId, fileId, opts));
      const blob = await blobFn(item.id, { inline: true });
      const url = URL.createObjectURL(blob);
      setPreviewUrls((prev) => ({ ...prev, [item.id]: url }));
    } catch (err) {
      onError?.(err.message);
    } finally {
      setLoadingPreviewId(null);
    }
  };

  useEffect(() => {
    items.forEach((item) => {
      if (item.is_image || item.is_video) {
        loadPreview(item);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, venteId]);

  const handleDownload = async (item) => {
    if (!venteId) return;
    setDownloadingId(item.id);
    try {
      const downloadFn = downloadFile
        ?? ((fileId, filename) => kkpService.downloadBriefFichier(venteId, fileId, filename));
      await downloadFn(item.id, item.nom);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="kkp-brief-fichiers kkp-brief-fichiers--viewer mt-3">
      <h6 className="fw-medium mb-2">{title}</h6>
      <div className="kkp-brief-fichiers__viewer-grid">
        {items.map((item) => (
          <div key={item.id} className="kkp-brief-fichiers__viewer-card">
            <div className="kkp-brief-fichiers__viewer-preview">
              {item.is_image && previewUrls[item.id] && (
                <img src={previewUrls[item.id]} alt={item.nom} className="kkp-brief-fichiers__preview-image" />
              )}
              {item.is_video && previewUrls[item.id] && (
                <video src={previewUrls[item.id]} controls className="kkp-brief-fichiers__preview-video">
                  <track kind="captions" />
                </video>
              )}
              {((item.is_image || item.is_video) && !previewUrls[item.id]) && (
                <div className="kkp-brief-fichiers__preview-placeholder">
                  {loadingPreviewId === item.id ? (
                    <AppSpinner size="sm" centered={false} />
                  ) : (
                    <Icon icon={fileIcon(item.mime)} width={28} />
                  )}
                </div>
              )}
              {!item.is_image && !item.is_video && (
                <div className="kkp-brief-fichiers__preview-placeholder">
                  <Icon icon={fileIcon(item.mime)} width={28} />
                </div>
              )}
            </div>
            <div className="kkp-brief-fichiers__viewer-meta">
              <span className="kkp-brief-fichiers__name" title={item.nom}>{item.nom}</span>
              <span className="kkp-brief-fichiers__size">{formatBriefFileSize(item.taille)}</span>
            </div>
            <AppButton
              type="button"
              size="sm"
              variant="outline-primary"
              className="w-100"
              onClick={() => handleDownload(item)}
              loading={downloadingId === item.id}
              icon="mdi:download"
            >
              Télécharger
            </AppButton>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KkpBriefAttachmentsViewer;
